import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';

function StaffApproval() {
  const [pendingStaff, setPendingStaff] = useState([]);
  const [message, setMessage] = useState('');
  const [denyReason, setDenyReason] = useState('');
  const [imageUrls, setImageUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const adminId = localStorage.getItem('playerId');

  useEffect(() => {
    const fetchPendingStaff = async () => {
      setLoading(true);
      try {
        console.log('Fetching with adminId:', adminId);
        const res = await fetch(`${API_BASE_URL}/pending-staff?adminId=${adminId}`, {
          headers: { 'player-id': adminId }
        });
        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }
        const data = await res.json();
        console.log('Fetched staff:', data);
        setPendingStaff(Array.isArray(data) ? data : []);
      } catch (error) {
        setMessage('Failed to fetch pending staff: ' + error.message);
        setPendingStaff([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPendingStaff();
  }, [adminId]);

  const handleImageUrlChange = (staffId, value) => {
    setImageUrls(prev => ({ ...prev, [staffId]: value }));
  };

  const approveStaff = async (staffId) => {
    const imageUrl = imageUrls[staffId] || '';
    if (!imageUrl) {
      setMessage('Please provide the official image URL before approving.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/admin-approve-staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'player-id': adminId
        },
        body: JSON.stringify({ staffId, imageUrl, adminId })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessage(data.message);
      setPendingStaff(pendingStaff.filter(s => s._id !== staffId));
      setImageUrls(prev => {
        const updated = { ...prev };
        delete updated[staffId];
        return updated;
      });
    } catch (error) {
      setMessage('Approval failed: ' + error.message);
    }
  };

  const denyStaff = async (staffId) => {
    if (!denyReason) {
      setMessage('Please provide a reason for denial.');
      return;
    }
    const confirmed = window.confirm(`Are you sure you want to deny ${pendingStaff.find(s => s._id === staffId)?.staffName}? Reason: ${denyReason}`);
    if (!confirmed) {
      setMessage('Denial cancelled.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/admin-deny-staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'player-id': adminId
        },
        body: JSON.stringify({ staffId, denyReason, adminId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || await res.text());
      setMessage(data.message || 'Staff denied successfully!');
      setPendingStaff(pendingStaff.filter(s => s._id !== staffId));
      setDenyReason('');
    } catch (error) {
      console.error('Deny error:', error);
      setMessage('Denial failed: ' + error.message);
    }
  };

  if (loading) {
    return <p style={{ textAlign: 'center', color: '#888', paddingTop: '60px' }}>Loading...</p>;
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px', paddingTop: '60px' }}>
      <h1 style={{ color: '#1E88E5', textAlign: 'center', marginBottom: '20px', fontSize: '1.8em' }}>Staff Approval</h1>
      {message && (
        <div style={{
          backgroundColor: message.includes('failed') ? '#EF5350' : '#90EE90',
          padding: '10px',
          borderRadius: '5px',
          textAlign: 'center',
          marginBottom: '20px',
          color: message.includes('failed') ? 'white' : 'black',
          fontWeight: 'bold'
        }}>
          {message}
        </div>
      )}
      {pendingStaff.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#888', fontSize: '16px' }}>No pending staff registrations.</p>
      ) : (
        pendingStaff.map(staff => (
          <div key={staff._id} style={{
            backgroundColor: '#f9f9f9',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #d9ead3',
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ color: '#4CAF50', marginBottom: '10px' }}>{staff.staffName}</h3>
            <p style={{ color: '#333', fontSize: '16px' }}>
              <strong>Owner:</strong> {staff.playerId?.username || 'Unknown'}
            </p>
            <p style={{ color: '#333', fontSize: '16px' }}>
              <strong>Submitted Art Link:</strong>{' '}
              <a
                href={staff.imageLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1E88E5', textDecoration: 'none' }}
                onMouseOver={e => e.target.style.color = '#1565C0'}
                onMouseOut={e => e.target.style.color = '#1E88E5'}
              >
                {staff.imageLink}
              </a>
            </p>
            <div style={{ marginTop: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#333', fontSize: '16px' }}>
                <strong>Official Image URL (DeviantArt):</strong>
              </label>
              <input
                type="text"
                value={imageUrls[staff._id] || ''}
                onChange={(e) => handleImageUrlChange(staff._id, e.target.value)}
                placeholder="Paste DeviantArt URL here"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  fontSize: '16px',
                  marginBottom: '15px'
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', maxWidth: '400px' }}>
                <input
                  type="text"
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                  placeholder="Reason for denial"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '5px',
                    border: '1px solid #ccc',
                    fontSize: '16px'
                  }}
                />
                <button
                  onClick={() => denyStaff(staff._id)}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: '#EF5350',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    transition: 'background-color 0.3s'
                  }}
                  onMouseOver={e => e.target.style.backgroundColor = '#e53935'}
                  onMouseOut={e => e.target.style.backgroundColor = '#EF5350'}
                >
                  Deny
                </button>
              </div>
              <button
                onClick={() => approveStaff(staff._id)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  transition: 'background-color 0.3s',
                  width: '100%',
                  maxWidth: '200px'
                }}
                onMouseOver={e => e.target.style.backgroundColor = '#45a049'}
                onMouseOut={e => e.target.style.backgroundColor = '#4CAF50'}
              >
                Approve
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default StaffApproval;